import React, { useEffect, useMemo, useState } from 'react';

import { GenericHandleListPanel } from '@/components/pocketbook';
import { Button } from '@/components/ui/button';
import {
  useArchiveHandleMutation,
  useUnarchiveHandleMutation,
} from '@/services/api/carriersApiService';

import type { Carrier, ContactHandle } from './types';

interface HandleListPanelProps {
  carrier: Carrier;
  onSelect: (handle: ContactHandle) => void;
  actions?: ('copy' | 'use' | 'link_defaults')[];
  onLinkDefaults?: (carrier: Carrier) => void;
  enableSelection?: boolean;
  onAttachSelected?: (carrier: Carrier, selected: ContactHandle[]) => void;
}

/**
 * Carrier-specific wrapper around GenericHandleListPanel
 * Simply passes carrier data to generic component
 */
export const HandleListPanel: React.FC<HandleListPanelProps> = ({
  carrier,
  onSelect,
  actions,
  onLinkDefaults,
  enableSelection = false,
  onAttachSelected,
}) => {
  const [archiveHandle] = useArchiveHandleMutation();
  const [unarchiveHandle] = useUnarchiveHandleMutation();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Compute default selected: one per handleType
  const defaultSelectedIds = useMemo(() => {
    const seenTypes = new Set<string>();
    const ids = new Set<string>();
    carrier.handles.forEach((h) => {
      const t = (h.handleType || '').toUpperCase();
      if (!seenTypes.has(t)) {
        ids.add(h.id);
        seenTypes.add(t);
      }
    });
    return ids;
  }, [carrier]);

  useEffect(() => {
    if (enableSelection) {
      // Auto-select one default handle per type when a carrier is selected
      setSelectedIds(new Set(defaultSelectedIds));
    }
  }, [enableSelection, defaultSelectedIds]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const handle = carrier.handles.find((h) => h.id === id);
      const type = (handle?.handleType || '').toUpperCase();

      if (next.has(id)) {
        next.delete(id);
        return next;
      }

      // Enforce only one per type by removing any existing selection of same type
      carrier.handles.forEach((h) => {
        if (next.has(h.id) && (h.handleType || '').toUpperCase() === type) {
          next.delete(h.id);
        }
      });
      next.add(id);
      return next;
    });
  };

  if (!enableSelection) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{carrier.name}</h3>
          {actions?.includes('link_defaults') && onLinkDefaults && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onLinkDefaults(carrier)}
            >
              Link defaults
            </Button>
          )}
        </div>
        <GenericHandleListPanel
          entityName=""
          handles={carrier.handles}
          onSelect={onSelect}
          actions={
            actions?.filter((a) => a !== 'link_defaults') as ('copy' | 'use')[]
          }
        />
      </div>
    );
  }

  const selectedHandles = carrier.handles.filter((h) => selectedIds.has(h.id));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">{carrier.name}</h3>
        <Button
          size="sm"
          onClick={() =>
            onAttachSelected && onAttachSelected(carrier, selectedHandles)
          }
          disabled={!selectedHandles.length}
        >
          Attach Selected Contacts
        </Button>
      </div>
      <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
        {carrier.handles.map((h) => {
          const value =
            h.value ||
            [h.addressLine1, h.city, h.state, h.zip].filter(Boolean).join(', ');
          const checked = selectedIds.has(h.id);
          return (
            <label
              key={h.id}
              htmlFor={`handle-${h.id}`}
              className="flex items-start gap-3 p-2 rounded border border-gray-200 hover:bg-gray-50"
            >
              <input
                id={`handle-${h.id}`}
                type="checkbox"
                className="mt-0.5"
                aria-label={`Select ${h.name}`}
                checked={checked}
                onChange={() => toggle(h.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">
                  {h.name}
                </div>
                <div className="text-sm text-gray-700 break-words">{value}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {h.handleType}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    archiveHandle({ handleId: h.id });
                  }}
                >
                  Archive
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    unarchiveHandle({ handleId: h.id });
                  }}
                >
                  Unarchive
                </Button>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};
